import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import { formatINR } from "../lib/utils-format";
import {
    ArrowDownLeft, ArrowDownRight, Gift, TrendingUp, Clock,
    CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronUp,
    FileCheck, Users, Zap, RotateCcw, Link2, Copy, Lock,
} from "lucide-react";

function initials(name = "") {
    return name.split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
}

/* ─── Wing queue member list ─────────────────────────────────────── */
function WingQueue({ members, side }) {
    const isLeft = side === "L";
    const avatarCls = isLeft
        ? "bg-[#10B1B6]/15 text-[#10B1B6]"
        : "bg-[#E45B80]/15 text-[#E45B80]";

    return (
        <div>
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-3 pb-2 border-b border-slate-100 mb-1">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Member</span>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center">Pairing Status</span>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right">Joined</span>
            </div>

            {members.length === 0 && (
                <div className="py-10 text-center text-slate-300 text-sm">
                    No {isLeft ? "left" : "right"} wing members yet
                </div>
            )}

            {members.map((m) => {
                const isPaired = m.pairing_status === "matched";
                return (
                    <div key={m.id} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center py-3 border-b border-slate-50">
                        {/* Member */}
                        <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-8 h-8 rounded-full flex-shrink-0 grid place-items-center text-[11px] font-black ${avatarCls}`}>
                                {initials(m.full_name)}
                            </div>
                            <div className="min-w-0">
                                <div className="font-bold text-slate-800 text-sm truncate">{m.full_name}</div>
                                <div className="text-[10px] text-slate-400 font-mono">{m.referral_code}</div>
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            {isPaired ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 whitespace-nowrap">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" /> Paired
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-500 whitespace-nowrap">
                                    <RotateCcw className="w-3 h-3" /> Carry-Fwd
                                </span>
                            )}
                        </div>

                        {/* Joined */}
                        <div className="text-[11px] text-slate-400 whitespace-nowrap text-right font-medium">
                            {new Date(m.activated_at).toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"})}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ─── main component ─────────────────────────────────────────────── */
export default function Pairing() {
    const { user } = useAuth();
    const [preview,  setPreview]  = useState(null);
    const [history,  setHistory]  = useState([]);
    const [members,  setMembers]  = useState(null);
    const [copied,   setCopied]   = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/pairing/preview"),
            api.get("/pairing/history"),
            api.get("/pairing/members-status"),
        ]).then(([p, h, m]) => {
            setPreview(p.data);
            setHistory(h.data);
            setMembers(m.data);
        }).catch(() => {});
    }, []);

    const totalPairs = history.reduce((a, h) => a + (h.pairs_credited || 0), 0);
    const totalNet   = history.reduce((a, h) => a + Number(h.net || 0), 0);
    const activeDays = history.filter(h => h.status === "credited").length;

    const p = preview;
    const isEligible = p && p.blockers.length === 0;
    const kycBlocked = p?.blockers.includes("kyc_not_approved");
    const noMembers  = p?.blockers.includes("no_left_members") || p?.blockers.includes("no_right_members");

    /* sidebar calculations */
    const leftCount  = p?.left_count  ?? 0;
    const rightCount = p?.right_count ?? 0;
    const maxWing    = Math.max(leftCount, rightCount, 1);
    const minWing    = Math.min(leftCount, rightCount);
    const symPct     = Math.round((minWing / maxWing) * 100);
    const symSide    = leftCount >= rightCount ? "Left" : "Right";

    const leftLeaders  = members?.left_members?.slice(0, 3)  ?? [];
    const rightLeaders = members?.right_members?.slice(0, 3) ?? [];

    const copyInvite = () => {
        const link = `${window.location.origin}/register?ref=${user?.referral_code}`;
        navigator.clipboard.writeText(link).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    /* volume bar percentages */
    const lPct = maxWing > 0 ? Math.min(100, (leftCount  / maxWing) * 100) : 0;
    const rPct = maxWing > 0 ? Math.min(100, (rightCount / maxWing) * 100) : 0;

    const leftMatched  = members?.left_members?.filter(m => m.pairing_status === "matched").length  ?? 0;
    const rightMatched = members?.right_members?.filter(m => m.pairing_status === "matched").length ?? 0;

    return (
        <AppShell>
            <div className="flex gap-5 items-start -m-4 md:-m-8 h-full overflow-hidden">

                {/* ── MAIN CONTENT (scrollable) ── */}
                <div className="flex-1 min-w-0 space-y-5 overflow-y-auto h-full p-4 md:p-8 pb-12">

                    {/* Header */}
                    <div className="flex justify-between items-center gap-4">
                        <div>
                            <div className="overline">Earnings</div>
                            <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5">
                                Pairing Bonus
                            </h1>
                        </div>
                        {user?.referral_code && (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 whitespace-nowrap border border-teal-200">
                                Sponsor ID: {user.referral_code}
                            </span>
                        )}
                    </div>

                    {/* ── Engine status banner ── */}
                    {p && (
                        <div className="rounded-3xl overflow-hidden flex shadow-sm">
                            <div className={`w-1.5 shrink-0 ${isEligible ? "bg-emerald-400" : "bg-[#F59E0B]"}`} />
                            <div className="flex-1 bg-[#0C2B5C] px-6 py-5 flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${isEligible ? "bg-emerald-400/20" : "bg-[#F59E0B]/15"}`}>
                                        {isEligible
                                            ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                            : <Lock className="w-5 h-5 text-[#F59E0B]" />
                                        }
                                    </div>
                                    <div>
                                        <span className={`text-[10px] font-extrabold uppercase tracking-widest ${isEligible ? "text-emerald-400" : "text-[#F59E0B]"}`}>
                                            {isEligible ? "ENGINE ACTIVE" : "ENGINE LOCK ACTIVE"}
                                        </span>
                                        <h3 className="text-white font-extrabold text-lg leading-tight">
                                            {isEligible ? "Eligible for Pairing Today" : "Pairing Engine Blocked"}
                                        </h3>
                                        <p className="text-white/50 text-xs mt-0.5">
                                            Nightly engine runs at midnight IST. Earn{" "}
                                            <span className="text-[#10B1B6] font-bold">₹{p.net_per_pair} net</span> per pair
                                            {p.admin_charge_pct != null && (
                                                <span className="text-white/30">
                                                    {" "}(₹{p.gross_per_pair} gross — {p.admin_charge_pct}% admin + {p.tds_pct}% TDS + {p.travel_fund_pct}% travel fund)
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 shrink-0">
                                    {p.blockers.map(b => {
                                        const labels = {
                                            kyc_not_approved:   "KYC not approved",
                                            no_left_members:    "No left members",
                                            no_right_members:   "No right members",
                                            no_new_pairs_today: "No new pairs today",
                                        };
                                        return (
                                            <span key={b} className="px-4 py-1.5 bg-white/10 text-white text-xs font-semibold rounded-full whitespace-nowrap border border-white/10">
                                                {labels[b] ?? b}
                                            </span>
                                        );
                                    })}
                                    {isEligible && (
                                        <span className="px-4 py-1.5 bg-emerald-400/20 text-emerald-300 text-xs font-semibold rounded-full whitespace-nowrap">
                                            {p.pairs_today} pairs ready
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Volume Symmetries + Calculator Matrix ── */}
                    {p && (
                        <div className="grid md:grid-cols-2 gap-5">

                            {/* Volume Symmetries */}
                            <div className="bg-white rounded-3xl shadow-sm p-6">
                                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Volume Symmetries</span>

                                <div className="mt-6 space-y-6">
                                    {/* Left wing */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-bold text-[#10B1B6] flex items-center gap-1">
                                                ✓ Left Wing volume
                                            </span>
                                            <span className="text-sm font-bold text-slate-700">{leftCount} active</span>
                                        </div>
                                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-[#10B1B6] rounded-full transition-all duration-500" style={{ width: `${lPct}%` }} />
                                        </div>
                                    </div>

                                    {/* Right wing */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-bold text-[#E45B80] flex items-center gap-1">
                                                ↘ Right Wing volume
                                            </span>
                                            <span className="text-sm font-bold text-slate-700">{rightCount} active</span>
                                        </div>
                                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-[#E45B80] rounded-full transition-all duration-500" style={{ width: `${rPct}%` }} />
                                        </div>
                                    </div>
                                </div>

                                {/* carry-forward footer */}
                                <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-400">
                                    Symmetric matching execution:{" "}
                                    {(p.carry_forward_left > 0 || p.carry_forward_right > 0) ? (
                                        <span className="text-[#10B1B6] font-bold">
                                            {p.carry_forward_left > 0
                                                ? `${p.carry_forward_left} Left unmatched (carried forward)`
                                                : `${p.carry_forward_right} Right unmatched (carried forward)`
                                            }
                                        </span>
                                    ) : (
                                        <span className="text-emerald-600 font-bold">Perfectly balanced</span>
                                    )}
                                </div>

                                {/* CTAs */}
                                {(kycBlocked || noMembers) && (
                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        {kycBlocked && (
                                            <Link to="/app/kyc"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#2172E5] text-white text-xs font-bold">
                                                <FileCheck className="w-3.5 h-3.5" /> Complete KYC
                                            </Link>
                                        )}
                                        {noMembers && !kycBlocked && (
                                            <Link to="/app/add-member"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#2172E5] text-white text-xs font-bold">
                                                <Users className="w-3.5 h-3.5" /> Add Members
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Calculator Matrix */}
                            <div className="bg-white rounded-3xl shadow-sm p-6">
                                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Calculator Matrix</span>

                                <div className="mt-5 space-y-0">
                                    {[
                                        { label: "Total matchable (min L, R)",    val: Math.min(p.left_count, p.right_count), cls: "text-slate-800 font-bold" },
                                        { label: "Already matched (prev. days)",  val: `-${p.already_matched}`,               cls: "text-[#E45B80] font-bold" },
                                        { label: "New eligible pairs",            val: p.new_eligible_pairs,                  cls: "text-[#10B1B6] font-bold" },
                                        { label: "Daily capped ceiling",          val: `Max ${p.daily_cap} / day`,            cls: "text-slate-800 font-extrabold" },
                                    ].map(({ label, val, cls }) => (
                                        <div key={label} className="flex items-center justify-between py-3 border-b border-slate-100">
                                            <span className="text-sm text-slate-500">{label}</span>
                                            <span className={`text-sm ${cls}`}>{val}</span>
                                        </div>
                                    ))}

                                    <div className="flex items-center justify-between py-3 border-b border-slate-100 mt-1">
                                        <span className="text-sm font-bold text-slate-800">Pairs execution today</span>
                                        <span className="text-2xl font-black text-[#10B1B6]">{p.pairs_today}</span>
                                    </div>

                                    {/* Earnings breakdown */}
                                    <div className="py-3 border-b border-slate-100">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-bold text-slate-800">Estimated gross</span>
                                            <span className="text-sm font-bold text-slate-700">{formatINR(p.estimated_gross)}</span>
                                        </div>
                                        {p.pairs_today > 0 && (
                                            <div className="mt-2 ml-3 space-y-1.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-orange-600 flex items-center gap-1">
                                                        <span className="text-[10px]">−</span> Admin charges ({p.admin_charge_pct}%)
                                                    </span>
                                                    <span className="text-xs font-semibold text-orange-600">−{formatINR(p.estimated_admin_charge)}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-red-500 flex items-center gap-1">
                                                        <span className="text-[10px]">−</span> TDS ({p.tds_pct}%)
                                                    </span>
                                                    <span className="text-xs font-semibold text-red-500">−{formatINR(p.estimated_tds)}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-blue-500 flex items-center gap-1">
                                                        <span className="text-[10px]">→</span> Travel fund ({p.travel_fund_pct}%)
                                                    </span>
                                                    <span className="text-xs font-semibold text-blue-500">{formatINR(p.estimated_travel_fund)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3">
                                        <span className="text-sm font-bold text-slate-800">Estimated net earnings</span>
                                        <span className={`text-2xl font-black ${p.pairs_today > 0 ? "text-[#10B1B6]" : "text-slate-300"}`}>
                                            {formatINR(p.estimated_net)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── 4 Stat Cards ── */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { label: "Total Pairs Credited", value: totalPairs,                                              sub: "across all matrix runs",                               valCls: "text-[#0C2B5C]", border: "border-b-[3px] border-[#7059FD]" },
                            { label: "Lifetime Net Earned",  value: formatINR(totalNet),                                    sub: `${activeDays} active match day`,                       valCls: "text-[#10B1B6]", border: "border-b-[3px] border-[#10B1B6]" },
                            { label: "Daily Cap",            value: p ? `${p.daily_cap} pairs` : "—",                      sub: `₹${p ? (p.net_per_pair * p.daily_cap).toLocaleString("en-IN") : 0} net daily max`, valCls: "text-[#0C2B5C]", border: "border-b-[3px] border-[#F59E0B]" },
                            { label: "Settlement Hold",      value: p ? `${p.settlement_days} days` : "—",                 sub: "then moves to balance",                                valCls: "text-[#0C2B5C]", border: "border-b-[3px] border-[#E45B80]" },
                        ].map(({ label, value, sub, valCls, border }) => (
                            <div key={label} className={`bg-white rounded-2xl shadow-sm p-5 ${border}`}>
                                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest leading-tight">{label}</span>
                                <div className={`text-3xl font-extrabold mt-2 leading-none ${valCls}`}>{value}</div>
                                <div className="text-xs text-slate-400 mt-1.5">{sub}</div>
                            </div>
                        ))}
                    </div>

                    {/* ── Sponsors & Carry-Forward Pools ── */}
                    {members && (
                        <>
                            {/* Header card — separate */}
                            <div className="bg-white rounded-3xl shadow-sm px-6 py-5 flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Wing Matrix Symmetry Details</span>
                                    <h3 className="text-2xl font-extrabold text-[#0C2B5C] mt-0.5">Sponsors &amp; Carry-Forward Pools</h3>
                                </div>
                                <span className="px-4 py-2 bg-purple-50 text-purple-600 text-xs font-bold rounded-full border border-purple-100">
                                    {members.matched_pairs} matches — {leftMatched}L + {rightMatched}R paired
                                </span>
                            </div>

                            {/* Two wing queue cards side by side */}
                            <div className="grid md:grid-cols-2 gap-5">
                                {/* Left wing queue card */}
                                <div className="bg-white rounded-3xl shadow-sm p-6">
                                    <div className="flex items-center justify-between mb-5">
                                        <h4 className="font-extrabold text-[#10B1B6] text-sm uppercase tracking-wide">
                                            Left Wing Queue ({members.left_total})
                                        </h4>
                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#10B1B6] bg-[#10B1B6]/10 border border-[#10B1B6]/20 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                            ✓ Left Wing
                                        </span>
                                    </div>
                                    <WingQueue members={members.left_members} side="L" />
                                </div>

                                {/* Right wing queue card */}
                                <div className="bg-white rounded-3xl shadow-sm p-6">
                                    <div className="flex items-center justify-between mb-5">
                                        <h4 className="font-extrabold text-[#E45B80] text-sm uppercase tracking-wide">
                                            Right Wing Queue ({members.right_total})
                                        </h4>
                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#E45B80] bg-[#E45B80]/10 border border-[#E45B80]/20 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                            ↘ Right Wing
                                        </span>
                                    </div>
                                    <WingQueue members={members.right_members} side="R" />
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── Execution Logs ── */}
                    <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
                        <div className="px-6 py-5 flex items-center justify-between border-b border-slate-100">
                            <div>
                                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Nightly Engine Runs</span>
                                <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-0.5">Execution logs</h3>
                            </div>
                            {history.length > 0 && (
                                <span className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-bold rounded-full">
                                    Last {history.length} run{history.length !== 1 ? "s" : ""}
                                </span>
                            )}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        <th className="py-3 px-5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-left whitespace-nowrap">Date</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Left/Right Vol</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Already Matched</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">New Eligible</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Pairs Credited</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Gross</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-orange-400 uppercase tracking-widest text-right whitespace-nowrap">Admin (15%)</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-red-400 uppercase tracking-widest text-right whitespace-nowrap">TDS (5%)</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-blue-400 uppercase tracking-widest text-right whitespace-nowrap">Travel Fund (5%)</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Net Credits</th>
                                        <th className="py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Carry L / R</th>
                                        <th className="py-3 px-5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.length === 0 && (
                                        <tr><td colSpan={12} className="py-16 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Gift className="w-8 h-8 text-slate-200" />
                                                <span className="text-slate-400 text-sm font-medium">No pairing runs yet.</span>
                                            </div>
                                        </td></tr>
                                    )}
                                    {history.map((h, i) => {
                                        const credited = h.status === "credited";
                                        const skipped  = h.status === "skipped";
                                        const evenBg   = i % 2 === 0 ? "bg-white" : "bg-slate-50/40";
                                        return (
                                            <tr key={i} className={`${evenBg} hover:bg-blue-50/30 transition-colors`}>
                                                <td className="py-4 px-5 font-bold text-slate-800 whitespace-nowrap">{h.run_date}</td>
                                                <td className="py-4 px-3 text-center text-xs font-mono">
                                                    <span className="text-[#10B1B6] font-bold">{h.left_total_snapshot}</span>
                                                    <span className="text-slate-300 mx-1">/</span>
                                                    <span className="text-[#E45B80] font-bold">{h.right_total_snapshot}</span>
                                                </td>
                                                <td className="py-4 px-3 text-center text-sm text-slate-600">{h.already_matched_after - h.pairs_credited}</td>
                                                <td className="py-4 px-3 text-center text-sm font-bold text-[#10B1B6]">{h.new_eligible}</td>
                                                <td className="py-4 px-3 text-center text-sm font-bold text-slate-700">{h.pairs_credited}</td>
                                                <td className="py-4 px-3 text-right text-sm font-mono text-slate-600 whitespace-nowrap">{formatINR(h.gross)}</td>
                                                <td className="py-4 px-3 text-right whitespace-nowrap">
                                                    {h.status === "credited" && h.gross > 0
                                                        ? <span className="text-sm font-mono font-semibold text-orange-600">−{formatINR(h.gross * 0.15)}</span>
                                                        : <span className="text-slate-200">—</span>}
                                                </td>
                                                <td className="py-4 px-3 text-right whitespace-nowrap">
                                                    {h.status === "credited" && h.gross > 0
                                                        ? <span className="text-sm font-mono font-semibold text-red-500">−{formatINR(h.gross * 0.05)}</span>
                                                        : <span className="text-slate-200">—</span>}
                                                </td>
                                                <td className="py-4 px-3 text-right whitespace-nowrap">
                                                    {h.status === "credited" && h.gross > 0
                                                        ? <span className="text-sm font-mono font-semibold text-blue-500">+{formatINR(h.gross * 0.05)}</span>
                                                        : <span className="text-slate-200">—</span>}
                                                </td>
                                                <td className="py-4 px-3 text-right text-sm font-mono font-bold text-[#10B1B6] whitespace-nowrap">{formatINR(h.net)}</td>
                                                <td className="py-4 px-3 text-right text-sm font-mono whitespace-nowrap">
                                                    <span className="text-[#10B1B6] font-bold">{h.carry_left}</span>
                                                    <span className="text-slate-300 mx-1">/</span>
                                                    <span className="text-[#E45B80] font-bold">{h.carry_right}</span>
                                                </td>
                                                <td className="py-4 px-5 text-center">
                                                    {credited ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" /> Credited
                                                        </span>
                                                    ) : skipped ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-500">
                                                            <RotateCcw className="w-3 h-3" /> Skipped
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-100">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" /> {h.status}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                {history.length > 0 && (
                                    <tfoot>
                                        <tr className="border-t border-slate-100 bg-slate-50/60">
                                            <td colSpan={4} className="py-3 px-5 text-xs font-bold text-slate-500">
                                                {history.length} runs · {activeDays} credited
                                            </td>
                                            <td className="py-3 px-3 text-center text-xs font-bold text-[#2172E5]">{totalPairs}</td>
                                            <td className="py-3 px-3 text-right text-xs font-mono text-slate-400">
                                                {formatINR(history.reduce((a, h) => a + Number(h.gross || 0), 0))}
                                            </td>
                                            <td className="py-3 px-3 text-right text-xs font-mono font-bold text-orange-600">
                                                −{formatINR(history.reduce((a, h) => a + Number(h.gross || 0) * 0.15, 0))}
                                            </td>
                                            <td className="py-3 px-3 text-right text-xs font-mono font-bold text-red-500">
                                                −{formatINR(history.reduce((a, h) => a + Number(h.gross || 0) * 0.05, 0))}
                                            </td>
                                            <td className="py-3 px-3 text-right text-xs font-mono font-bold text-blue-500">
                                                +{formatINR(history.reduce((a, h) => a + Number(h.gross || 0) * 0.05, 0))}
                                            </td>
                                            <td className="py-3 px-3 text-right text-xs font-mono font-bold text-[#10B1B6]">{formatINR(totalNet)}</td>
                                            <td colSpan={2} />
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>

                </div>

                {/* ── RIGHT SIDEBAR (fixed height, no scroll) ── */}
                <div className="w-72 hidden xl:flex flex-col gap-4 shrink-0 h-full overflow-y-auto no-scrollbar p-4 md:p-8 md:pl-0">
                    <div className="bg-white rounded-3xl shadow-sm p-5 flex flex-col gap-5">

                        {/* Wing Ratio Status */}
                        <div>
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Wing Ratio Status</span>
                            <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-1">Network Symmetry</h3>

                            <div className="mt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-slate-600">Wing symmetry</span>
                                    <span className="text-sm font-bold text-[#2172E5]">{symPct}% matched</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-[#2172E5] rounded-full transition-all duration-500" style={{ width: `${symPct}%` }} />
                                </div>
                                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                    Matching {minWing} pairs of your {maxWing} potential {symSide} Wing channels.
                                </p>
                            </div>
                        </div>

                        {/* Team Channels */}
                        <div className="border-t border-slate-100 pt-4">
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Team Channels</span>
                            <div className="mt-3 space-y-1">

                                {/* Left Wing Leaders */}
                                <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer">
                                    <span className="text-sm font-bold text-[#10B1B6]">Left Wing Leaders</span>
                                    <div className="flex -space-x-1.5">
                                        {leftLeaders.map((m, i) => (
                                            <div key={m.id ?? i}
                                                className="w-6 h-6 rounded-full bg-[#10B1B6]/15 text-[#10B1B6] text-[9px] font-black grid place-items-center ring-2 ring-white">
                                                {initials(m.full_name)}
                                            </div>
                                        ))}
                                        {leftLeaders.length === 0 && <span className="text-xs text-slate-300">—</span>}
                                    </div>
                                </div>

                                {/* Right Wing Growth */}
                                <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer">
                                    <span className="text-sm font-bold text-[#E45B80]">Right Wing Growth</span>
                                    <div className="flex -space-x-1.5">
                                        {rightLeaders.map((m, i) => (
                                            <div key={m.id ?? i}
                                                className="w-6 h-6 rounded-full bg-[#E45B80]/15 text-[#E45B80] text-[9px] font-black grid place-items-center ring-2 ring-white">
                                                {initials(m.full_name)}
                                            </div>
                                        ))}
                                        {rightLeaders.length === 0 && <span className="text-xs text-slate-300">—</span>}
                                    </div>
                                </div>

                                {/* Direct Referrals */}
                                <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer">
                                    <span className="text-sm font-bold text-[#7059FD]">Direct Referrals</span>
                                    <div className="flex -space-x-1.5">
                                        {[...leftLeaders.slice(0,1), ...rightLeaders.slice(0,1)].map((m, i) => (
                                            <div key={i}
                                                className="w-6 h-6 rounded-full bg-[#7059FD]/15 text-[#7059FD] text-[9px] font-black grid place-items-center ring-2 ring-white">
                                                {initials(m.full_name)}
                                            </div>
                                        ))}
                                        {(leftLeaders.length + rightLeaders.length === 0) && <span className="text-xs text-slate-300">—</span>}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Copy Invite Link */}
                        <div className="border-t border-slate-100 pt-4">
                            <button
                                onClick={copyInvite}
                                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-[#2172E5] hover:text-blue-700 transition-colors"
                            >
                                {copied
                                    ? <><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Copied!</>
                                    : <><Copy className="w-4 h-4" /> + Copy Partner Invite Link</>
                                }
                            </button>
                        </div>

                    </div>
                </div>

            </div>
        </AppShell>
    );
}
